import React, { useEffect, useState, useRef, useCallback } from "react";
import { serverDomain } from "../../../config";
import { FaArrowUp, FaArrowDown } from "react-icons/fa";
import "../../../styles/AdminLogsPage.css";

const AdminLogsPage = () => {
  const today = new Date();
  const lastWeek = new Date();
  lastWeek.setDate(today.getDate() - 7);

  const formatDate = (date) => date.toISOString().split("T")[0];

  const [logs, setLogs] = useState([]);
  const [startDate, setStartDate] = useState(formatDate(lastWeek));
  const [endDate, setEndDate] = useState(formatDate(today));
  const [userIdFilter, setUserIdFilter] = useState("");

  const [page, setPage] = useState(1);
  const [pageInput, setPageInput] = useState("1");

  // ✅ можно менять сколько строк показывать
  const [limit, setLimit] = useState(25);

  const [loading, setLoading] = useState(false);
  const [sortColumn, setSortColumn] = useState("time");
  const [sortOrder, setSortOrder] = useState("desc");

  const reqIdRef = useRef(0);

  const fetchLogs = useCallback(async () => {
    const myId = ++reqIdRef.current;
    setLoading(true);

    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams({
        start_date: startDate,
        end_date: endDate,
        page: String(page),
        limit: String(limit),
      });

      if (userIdFilter.trim() !== "") {
        params.append("user_id", userIdFilter.trim());
      }

      const res = await fetch(`${serverDomain}/api/admin/logs?${params.toString()}`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await res.json();
      if (reqIdRef.current === myId) {
        setLogs(data.logs || []);
      }
    } catch (e) {
      if (reqIdRef.current === myId) console.error("Ошибка загрузки логов:", e);
    } finally {
      if (reqIdRef.current === myId) setLoading(false);
    }
  }, [startDate, endDate, page, limit, userIdFilter]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // при смене фильтров — на 1 страницу
  useEffect(() => {
    setPage(1);
    setPageInput("1");
  }, [startDate, endDate, userIdFilter, limit]);

  const handleUpdateClick = () => {
    setPage(1);
    setPageInput("1");
    fetchLogs();
  };

  const handlePrevPage = () => {
    setPage((prev) => {
      const newPage = Math.max(prev - 1, 1);
      setPageInput(String(newPage));
      return newPage;
    });
  };

  const handleNextPage = () => {
    setPage((prev) => {
      const newPage = prev + 1;
      setPageInput(String(newPage));
      return newPage;
    });
  };

  const handlePageInputChange = (e) => {
    const v = e.target.value;
    setPageInput(v);
    const n = Number(v);
    if (!isNaN(n) && n > 0) setPage(n);
  };

  const handlePageInputBlur = () => {
    if (!pageInput) {
      setPage(1);
      setPageInput("1");
    }
  };

  const handleSort = (column) => {
    const newOrder = sortColumn === column && sortOrder === "asc" ? "desc" : "asc";
    setSortColumn(column);
    setSortOrder(newOrder);

    const sorted = [...logs].sort((a, b) => {
      let A = a[column];
      let B = b[column];

      if (column === "time") {
        A = new Date(A);
        B = new Date(B);
        return newOrder === "asc" ? A - B : B - A;
      }
      if (column === "amount") {
        A = Number(A) || 0;
        B = Number(B) || 0;
        return newOrder === "asc" ? A - B : B - A;
      }
      if (typeof A === "string" && typeof B === "string") {
        return newOrder === "asc" ? A.localeCompare(B) : B.localeCompare(A);
      }
      return 0;
    });

    setLogs(sorted);
  };

  const renderSortIcon = (column) => {
    if (column !== sortColumn) return null;
    return sortOrder === "asc" ? <FaArrowUp /> : <FaArrowDown />;
  };

  return (
    <div className="fl-adminlogs">
      <div className="fl-adminlogs__head">
        <h2 className="fl-adminlogs__title">Лог успешных операций</h2>
      </div>

      {/* Toolbar */}
      <div className="fl-toolbar fl-adminlogs__toolbar">
        <input className="fl-input" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        <span className="fl-toolbar__dash">-</span>
        <input className="fl-input" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />

        <input
          className="fl-input fl-input--wide"
          type="text"
          placeholder="Поиск по User ID"
          value={userIdFilter}
          onChange={(e) => setUserIdFilter(e.target.value)}
        />

        <select className="fl-select" value={limit} onChange={(e) => setLimit(Number(e.target.value))} title="Строк на странице">
          <option value={25}>25</option>
          <option value={50}>50</option>
          <option value={100}>100</option>
        </select>

        <button className="fl-btn" onClick={handleUpdateClick} disabled={loading}>
          {loading ? "Загрузка..." : "Обновить"}
        </button>

        <div className="fl-adminlogs__pager">
          <button className="fl-btn fl-btn--ghost" onClick={handlePrevPage} disabled={page === 1}>
            ‹
          </button>

          <span className="fl-adminlogs__pageLabel">Стр.</span>
          <input
            className="fl-input fl-input--page"
            type="number"
            value={pageInput}
            onChange={handlePageInputChange}
            onBlur={handlePageInputBlur}
          />
          <button className="fl-btn fl-btn--ghost" onClick={handleNextPage}>
            ›
          </button>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="loading-container">
          <div className="loader" />
        </div>
      ) : logs.length > 0 ? (
        <div className="fl-table-wrap">
          <table className="fl-table">
            <thead>
              <tr>
                <th onClick={() => handleSort("time")} className="fl-sort">
                  Время {renderSortIcon("time")}
                </th>
                <th>Mail работника</th>
                <th>TG работника</th>
                <th>Оффер</th>
                <th>Поток</th>
                <th>Цель</th>
                <th onClick={() => handleSort("amount")} className="fl-sort">
                  Сумма {renderSortIcon("amount")}
                </th>
                <th>User ID</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log, idx) => (
                <tr key={`${log.transaction_id || "t"}_${log.time || idx}`}>
                  <td>{new Date(log.time).toLocaleString()}</td>
                  <td>{log.worker_email || "—"}</td>
                  <td>{log.worker_tg || "—"}</td>
                  <td>{log.offer || "—"}</td>
                  <td>{log.stream || "—"}</td>
                  <td>{log.goal || "—"}</td>
                  <td>{log.amount ?? "—"}</td>
                  <td>{log.user_id ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="fl-empty">Нет данных за выбранный период.</div>
      )}
    </div>
  );
};

export default AdminLogsPage;